import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { CheckCircle2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const nav = useNavigate();
  const { refresh } = useAuth();

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          await refresh();
          return;
        }
        if (data.status === "expired") { setStatus("expired"); return; }
        if (attempts < 8) setTimeout(poll, 2000);
        else setStatus("timeout");
      } catch {
        if (attempts < 5) setTimeout(poll, 2000);
        else setStatus("error");
      }
    };
    poll();
  }, [sessionId, refresh]);

  return (
    <AppShell>
      <div className="max-w-xl mx-auto px-6 py-20 text-center">
        {status === "checking" && (
          <>
            <div className="w-16 h-16 rounded-full bg-[#F3EFEA] mx-auto mb-6 animate-pulse" />
            <h1 className="font-display text-3xl">Confirming your payment…</h1>
            <p className="text-[#7A736E] mt-2">Just a moment.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="w-16 h-16 rounded-full bg-[#D8E2D8] flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-8 h-8 text-[#3A5A4A]" />
            </div>
            <h1 className="font-display text-3xl" data-testid="billing-success-title">Welcome to Hope Chest+.</h1>
            <p className="text-[#7A736E] mt-2 mb-8">You now have unlimited Hope Chests and family collaboration.</p>
            <Button onClick={() => nav("/dashboard")} className="rounded-full bg-[#3A5A4A] hover:bg-[#2C4538]" data-testid="billing-success-go-dashboard">Back to Dashboard</Button>
          </>
        )}
        {(status === "expired" || status === "error" || status === "timeout") && (
          <>
            <h1 className="font-display text-3xl">We couldn't confirm your payment.</h1>
            <p className="text-[#7A736E] mt-2 mb-8">Please try again, or contact support if you believe this is a mistake.</p>
            <Button onClick={() => nav("/pricing")} className="rounded-full bg-[#3A5A4A] hover:bg-[#2C4538]">Back to pricing</Button>
          </>
        )}
      </div>
    </AppShell>
  );
}
